import React, { useRef, useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import PropTypes from 'prop-types';
import { Box, Grow, IconButton, Paper, Typography, useTheme } from '@mui/material';
import filetype from '../images/Filetype-conversion.png';
import datatype from '../images/Datatype-conversion.png';
import filtering from '../images/Filtering.png';
import { Close } from '@mui/icons-material';
import { useThemeContext } from '../../Context/ThemeContext';

const TransformationNode = ({ id, data, deleteNode }) => {
  const [isHovered, setIsHovered] = useState(false);
  const theme = useTheme();
  const { darkMode } = useThemeContext();
  const [isHandleHover, setIsHandleHover] = useState(false);
  const hoverTimeout = useRef(null);

  const handleMouseEnter = () => {
    hoverTimeout.current = setTimeout(() => setIsHovered(true), 400);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeout.current);
    setIsHovered(false);
    setIsHandleHover(false);
  };

  const [mainLabel, typeLabel] = data.label.includes('(')
    ? data.label.split(' (')
    : [data.label, ''];

  const displayType = typeLabel.replace(')', '').trim();

  let icon = filtering;
  if (mainLabel === 'File-type Conversion') icon = filetype;
  else if (mainLabel === 'Data-type Conversion') icon = datatype;

  const ICON_SIZE = 32;
  const NODE_SIZE = 56;
  const showOverlay = isHovered && !isHandleHover;

  return (
    <Box
      sx={{
        width: NODE_SIZE,
        height: NODE_SIZE,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 1,
        boxShadow: 2,
        bgcolor: darkMode ? theme.palette.background.paper : '#fff',
        border: `1.5px solid ${
          darkMode ? theme.palette.primary.main : '#549bde'
        }`,
        zIndex: isHovered ? 100 : 1,
        transition: 'box-shadow 0.2s',
        cursor: 'pointer',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box
        sx={{
          width: ICON_SIZE,
          height: ICON_SIZE,
          borderRadius: 1,
          bgcolor: darkMode ? theme.palette.background.default : '#f5f6fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img src={icon} alt="Transformation Node" width={28} height={28} />
      </Box>

      <Grow
        in={showOverlay}
        timeout={{ enter: 300, exit: 250 }}
        style={{
          transitionDelay: showOverlay ? '100ms' : '0ms',
        }}
      >
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            top: -8,
            left: -12,
            minWidth: 200,
            minHeight: 50,
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            borderRadius: 1,
            bgcolor: darkMode ? theme.palette.background.paper : '#fff',
            border: `1.5px solid ${
              darkMode ? theme.palette.primary.main : '#549bde'
            }`,
            zIndex: 200,
            transition: 'opacity 0.15s',
            pointerEvents: 'auto',
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1,
              bgcolor: darkMode ? theme.palette.background.default : '#f5f6fa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
            }}
          >
            <img src={icon} alt="Transformation Node" width={34} height={34} />
          </Box>
          <Box sx={{ ml: 1 }}>
            <Typography
              sx={{
                color: darkMode ? theme.palette.text.secondary : '#757575',
                fontSize: '0.75rem',
                marginBottom: '0.25rem',
                wordBreak: 'break-word',
              }}
            >
              {displayType}
            </Typography>
            <Typography
              sx={{
                color: darkMode ? theme.palette.text.primary : '#212121',
                fontSize: '0.875rem',
                fontWeight: 600,
                wordBreak: 'break-word',
              }}
            >
              {mainLabel}
            </Typography>
          </Box>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              deleteNode(id, data.label);
            }}
            size="small"
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              background: darkMode ? '#353535' : '#fff',
              color: theme.palette.error.main,
              width: '1.25rem',
              height: '1.25rem',
              borderRadius: '50%',
              boxShadow: 1,
              zIndex: 2,
              '&:hover': {
                background: darkMode
                  ? theme.palette.error.dark
                  : theme.palette.error.dark,
                color: '#fff',
              },
            }}
          >
            <Close sx={{ fontSize: '0.75rem' }} />
          </IconButton>
        </Paper>
      </Grow>

      {(!isHovered || isHandleHover) && (
        <>
          <Handle
            type="source"
            position={Position.Right}
            onMouseEnter={() => setIsHandleHover(true)}
            onMouseLeave={() => setIsHandleHover(false)}
            style={{
              zIndex: 300,
              background: darkMode ? theme.palette.primary.main : '#0056D2',
            }}
          />
          <Handle
            type="target"
            position={Position.Left}
            onMouseEnter={() => setIsHandleHover(true)}
            onMouseLeave={() => setIsHandleHover(false)}
            style={{
              zIndex: 300,
              background: darkMode ? theme.palette.primary.main : '#0056D2',
            }}
          />
        </>
      )}
    </Box>
  );
};

TransformationNode.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.shape({
    label: PropTypes.string.isRequired,
  }).isRequired,
  deleteNode: PropTypes.func.isRequired,
};

export default TransformationNode;
